import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import FooterSection from '../home/components/FooterSection';
import SEOHead from '../../components/feature/SEOHead';
import { SITE_URL } from '../../utils/seo';

const iletisimJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'VergeIO İletişim',
  url: `${SITE_URL}/iletisim`,
  description: 'VergeOS ekibiyle iletişime geçin. Demo, fiyatlandırma veya teknik bilgi için uzman ekibimiz 24 saat içinde yanıt verir.',
};

export default function IletisimPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'message') setCharCount(value.length);
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.company) {
      setError('Lütfen zorunlu alanları doldurun.');
      return;
    }
    if (formData.message.length > 500) {
      setError('Mesaj 500 karakteri aşamaz.');
      return;
    }
    setError('');

    const body = new URLSearchParams();
    body.append('firstName', formData.firstName);
    body.append('lastName', formData.lastName);
    body.append('email', formData.email);
    body.append('company', formData.company);
    body.append('phone', formData.phone);
    body.append('message', formData.message);

    // sendBeacon: tamamen non-blocking, CORS preflight yok, anlık
    const blob = new Blob([body.toString()], { type: 'application/x-www-form-urlencoded' });
    navigator.sendBeacon('https://readdy.ai/api/form/d7cfj5kkpdh0a4advheg', blob);

    // Anında başarı ekranına geç
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <SEOHead
        title="İletişim | VergeOS Demo ve Teknik Bilgi"
        description="VergeOS ekibiyle iletişime geçin. Demo talep edin, fiyatlandırma bilgisi alın veya teknik sorularınızı sorun. Uzman ekibimiz iş günlerinde 24 saat içinde yanıt verir."
        keywords="VergeIO iletişim, demo talep, fiyatlandırma, teknik destek, VMware alternatifi iletişim"
        canonicalPath="/iletisim"
        jsonLd={iletisimJsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section
        className="relative py-20 bg-cover bg-center"
        style={{ backgroundImage: "url('https://public.readdy.ai/ai/img_res/c3433cb9f5ce6db459deba91c31d1d47.jpg')" }}
      >
        <div className="absolute inset-0 bg-[#1a1a2e]/70" />
        <div className="relative z-10 text-center px-6 w-full">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white text-xs font-semibold px-3 py-1.5 rounded-full mb-6">
            <i className="ri-mail-line text-[#e8600a]"></i>
            Bizimle İletişime Geçin
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">İletişim</h1>
          <p className="text-lg text-white/80 max-w-xl mx-auto">
            Ekibimiz en kısa sürede size geri dönecek.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-[#f7f8fa]">
        <div className="max-w-[1100px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">

          {/* Left Info */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-[#1a1a2e] mb-3">Nasıl yardımcı olabiliriz?</h2>
              <p className="text-sm text-gray-500 leading-relaxed">
                VergeOS hakkında sorularınız mı var? Demo talep etmek, fiyatlandırma veya teknik bilgi almak için formu doldurun — uzman ekibimiz yanıt versin.
              </p>
            </div>

            <div className="space-y-5">
              {[
                {
                  icon: 'ri-time-line',
                  title: 'Hızlı Yanıt',
                  desc: 'İş günlerinde 24 saat içinde geri dönüyoruz.',
                },
                {
                  icon: 'ri-customer-service-2-line',
                  title: 'Uzman Destek',
                  desc: 'Çözüm mimarlarımız altyapı ihtiyaçlarınıza özel rehberlik sunar.',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: 'Gizlilik Güvencesi',
                  desc: 'Bilgileriniz yalnızca sizinle iletişim kurmak için kullanılır.',
                },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-orange-50 flex-shrink-0">
                    <i className={`${item.icon} text-[#e8600a] text-lg`}></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#1a1a2e] mb-0.5">{item.title}</div>
                    <div className="text-xs text-gray-500 leading-relaxed">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Form */}
          <div className="lg:col-span-3 bg-white rounded-xl border border-gray-100 p-8">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-green-50 rounded-full">
                  <i className="ri-checkbox-circle-line text-green-500 text-4xl"></i>
                </div>
                <h3 className="text-xl font-bold text-[#1a1a2e] mb-2">Mesajınız İletildi!</h3>
                <p className="text-gray-500 text-sm mb-6">En kısa sürede sizinle iletişime geçeceğiz.</p>
                <button
                  onClick={() => navigate('/')}
                  className="bg-[#e8600a] text-white px-6 py-2 rounded-md text-sm font-semibold hover:bg-[#cc5208] transition-colors cursor-pointer whitespace-nowrap"
                >
                  Ana Sayfaya Dön
                </button>
              </div>
            ) : (
              <form
                id="iletisim-form"
                data-readdy-form="true"
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <h3 className="text-lg font-bold text-[#1a1a2e] mb-2">Bize Ulaşın</h3>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Ad <span className="text-red-400">*</span></label>
                    <input
                      type="text"
                      name="firstName"
                      placeholder="Adınız"
                      value={formData.firstName}
                      onChange={handleChange}
                      className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-[#e8600a] transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Soyad <span className="text-red-400">*</span></label>
                    <input
                      type="text"
                      name="lastName"
                      placeholder="Soyadınız"
                      value={formData.lastName}
                      onChange={handleChange}
                      className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-[#e8600a] transition-colors"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">İş E-posta <span className="text-red-400">*</span></label>
                  <input
                    type="email"
                    name="email"
                    placeholder="ornek@sirket.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-[#e8600a] transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Şirket <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    name="company"
                    placeholder="Şirket Adı"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-[#e8600a] transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Telefon <span className="text-gray-400 font-normal">(İsteğe bağlı)</span></label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="+90 5XX XXX XX XX"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-[#e8600a] transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Mesajınız <span className="text-gray-400 font-normal">(İsteğe bağlı)</span></label>
                  <textarea
                    name="message"
                    placeholder="Nasıl yardımcı olabiliriz?"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    maxLength={500}
                    className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-[#e8600a] transition-colors resize-none"
                  />
                  <div className={`text-right text-xs mt-1 ${charCount > 480 ? 'text-red-400' : 'text-gray-400'}`}>
                    {charCount}/500
                  </div>
                </div>

                {error && <p className="text-red-500 text-sm">{error}</p>}

                <button
                  type="submit"
                  disabled={charCount > 500}
                  className="w-full bg-[#e8600a] text-white py-3 rounded-md font-semibold text-sm hover:bg-[#cc5208] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  Gönder
                </button>

                <p className="text-xs text-gray-400 text-center">
                  Bilgileriniz yalnızca sizinle iletişim kurmak amacıyla kullanılır.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-14 bg-white border-t border-gray-100">
        <div className="max-w-[1100px] mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { stat: '%85', label: 'Altyapı Maliyet Azalması' },
            { stat: '7 dk', label: 'Kurulum Süresi' },
            { stat: '500+', label: 'Mutlu Müşteri' },
            { stat: '24s', label: 'Yanıt Süresi' },
          ].map((s, i) => (
            <div key={i}>
              <div className="text-3xl font-bold text-[#e8600a] mb-1">{s.stat}</div>
              <div className="text-xs text-gray-500">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <FooterSection />
    </div>
  );
}
